import { FC } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { menuItemClasses } from "@mui/material/MenuItem";
import moment from "moment";
import { Link } from "react-router-dom";
import { usePopover } from "hooks/use-popover";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { WebForm } from "../WebFormsService";
import webFormsStyles from "../WebForms.styles";
import webFormsTokens, {
  PHONE_SM_QUERY,
  webFormsResponsiveTokens,
  webFormsTemplateTones,
  WebFormsTemplateTone,
} from "../WebForms.tokens";
import { KebabGlyph, WebFormGlyph } from "./WebFormsIcons";
import { SparkleGlyph } from "../AiBuilder/components/AiWebFormIcons";
import { aiBuilderPath } from "../AiBuilder/AiWebFormHelper";

const styles = webFormsStyles;
const { table } = webFormsTokens;
const { phone, tablet } = webFormsResponsiveTokens;

/** Glyph inside the row's icon tile — one size per reference column. */
const rowIconSx = {
  width: { xs: phone.card.iconSize.std, sm: tablet.row.iconSize, md: table.iconSize },
  height: { xs: phone.card.iconSize.std, sm: tablet.row.iconSize, md: table.iconSize },
  [PHONE_SM_QUERY]: {
    width: phone.card.iconSize.sm,
    height: phone.card.iconSize.sm,
  },
};

/** Kebab glyph — likewise. */
const kebabIconSx = {
  width: {
    xs: phone.card.kebabIconSize.std,
    sm: tablet.row.kebabIconSize,
    md: table.kebabIconSize,
  },
  height: {
    xs: phone.card.kebabIconSize.std,
    sm: tablet.row.kebabIconSize,
    md: table.kebabIconSize,
  },
  [PHONE_SM_QUERY]: {
    width: phone.card.kebabIconSize.sm,
    height: phone.card.kebabIconSize.sm,
  },
};

/**
 * Picks the row's tint from its template name so every form built from the same
 * template is coloured alike. A name-derived index keeps that stable across
 * pages without the API having to hand us a colour.
 */
const getTemplateTone = (templateName?: string): WebFormsTemplateTone => {
  const name = templateName ?? "";
  let sum = 0;
  // Indexed rather than iterated: the project compiles to ES5 without
  // `downlevelIteration`, so spreading a string is a compile error.
  for (let index = 0; index < name.length; index += 1) {
    sum += name.charCodeAt(index);
  }
  return webFormsTemplateTones[sum % webFormsTemplateTones.length];
};

interface WebFormRowProps {
  form: WebForm;
  onRenameClick: (form: WebForm) => void;
  onDeleteClick: (form: WebForm) => void;
  onUpdateClick: (form: WebForm) => void;
}

/**
 * One web form row: icon tile with the form name over its link caption, the
 * template chip, when it was created, and the overflow trigger.
 */
export const WebFormRow: FC<WebFormRowProps> = ({
  form,
  onRenameClick,
  onDeleteClick,
  onUpdateClick,
}) => {
  const popover = usePopover<HTMLButtonElement>();
  const tone = getTemplateTone(form.templateName);
  const isAiDesign = Boolean(form.aiDesignId || (!form.unlayerJson && form.htmlKey));
  const targetUrl =
    form.aiDesignId && form.aiSessionId
      ? aiBuilderPath({
          sessionId: form.aiSessionId,
          templateId: form.templateId,
          signerId: form.signerId,
          name: form.name,
        })
      : `${viewpaths.webFormsViewPath}/builder/${form.id}`;

  const handleMenuItemClick = (action: "Rename" | "Update" | "Delete") => {
    popover.handleClose();
    if (action === "Rename") {
      onRenameClick(form);
    } else if (action === "Update") {
      onUpdateClick(form);
    } else {
      onDeleteClick(form);
    }
  };

  return (
    <>
      <Box
        role="row"
        sx={styles.row}
        onClick={() => onUpdateClick(form)}
      >
        <Box role="cell" sx={styles.rowLead}>
          <Box sx={styles.iconTile(tone)}>
            <WebFormGlyph sx={{ ...rowIconSx, color: tone.stroke }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
              <Tooltip title={form.name} arrow>
                {/* The row's only keyboard-operable "open" control (2.1.1/4.1.2);
                    stopPropagation so it doesn't double-fire the row's mouse nav. */}
                <Box
                  component={Link}
                  to={targetUrl}
                  aria-label={`Open web form ${form.name}`}
                  onClick={(event: any) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onUpdateClick(form);
                  }}
                  sx={styles.rowTitle}
                >
                  {form.name}
                </Box>
              </Tooltip>
              {isAiDesign && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "6px",
                    py: "2px",
                    borderRadius: "9999px",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    bgcolor: "rgba(235, 77, 45, 0.08)",
                    color: "#eb4d2d",
                    flexShrink: 0,
                    lineHeight: 1.4,
                  }}
                >
                  <SparkleGlyph sx={{ width: 10, height: 10 }} />
                  AI DESIGN
                </Box>
              )}
            </Box>
            {/*
              The phone card has no CREATED column, so the timestamp takes the
              caption's place under the name; from `sm` up the caption is back
              and the column carries the date again.
            */}
            <Typography component="div" sx={styles.rowSub}>
              <Box component="span" sx={styles.rowSubLink}>
                {table.subLabel}
              </Box>
              <Box component="span" sx={styles.rowSubCreated}>
                {phone.card.subPrefix}
                {moment(form.createdOn).format("MMM D, YYYY h:mm A")}
              </Box>
            </Typography>
          </Box>
        </Box>

        <Box role="cell" sx={styles.templateColumn}>
          {form.templateName && (
            <Tooltip title={form.templateName} arrow describeChild>
              <Box sx={styles.templateChip(tone)}>
                <Box component="span" sx={styles.templateDot(tone)} />
                <Box component="span" sx={styles.templateLabel(tone)}>
                  {form.templateName}
                </Box>
              </Box>
            </Tooltip>
          )}
        </Box>

        <Typography role="cell" component="div" sx={styles.createdColumn}>
          <Box component="span" sx={styles.createdPrefix}>
            Created on{" "}
          </Box>
          <Box component="span" sx={styles.createdValue}>
            {moment(form.createdOn).format("MMM D, YYYY h:mm A")}
          </Box>
        </Typography>

        <Box role="cell" sx={styles.kebabColumn}>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              popover.anchorRef.current = event.currentTarget;
              popover.handleOpen();
            }}
            ref={popover.anchorRef}
            sx={styles.kebabButton}
            aria-label={`Options for web form ${form.name}`}
          >
            <KebabGlyph sx={kebabIconSx} />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClose={popover.handleClose}
        sx={{ [`& .${menuItemClasses.root}`]: { fontSize: 14 } }}
      >
        <MenuItem onClick={() => handleMenuItemClick("Rename")}>
          Rename
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("Update")}>
          Update
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("Delete")}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default WebFormRow;
